import {
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
} from '@tanstack/react-router';
import type { Origin } from './state/session.ts';
import { Entry } from './screens/Entry.tsx';
import { Home } from './screens/Home.tsx';
import { Landing } from './screens/Landing.tsx';
import { Path } from './screens/Path.tsx';
import { isGameId, type GameId } from '@ezishizi/learning-core';
import { DiagPanel } from './components/DiagPanel.tsx';
import { isPathMode } from './lib/adventure.ts';
import type { PathMode } from './lib/path.ts';

// A session can only ever *start* on Entry, Home, Path or Landing — everything a stop or a
// nav link opens from there is code-split so the first paint doesn't wait on it. Chunks are
// warmed during idle time after mount (see preloadLazyRoutes, called from main.tsx) so a tap
// on a stop is never the first time a chunk is requested.
const Play = lazyRouteComponent(() => import('./screens/Play.tsx'), 'Play');
const PracticeRunner = lazyRouteComponent(
  () => import('./screens/PracticeRunner.tsx'),
  'PracticeRunner',
);
const StoryPlayer = lazyRouteComponent(() => import('./screens/StoryPlayer.tsx'), 'StoryPlayer');
const Result = lazyRouteComponent(() => import('./screens/Result.tsx'), 'Result');
const Parent = lazyRouteComponent(() => import('./screens/Parent.tsx'), 'Parent');
const Auth = lazyRouteComponent(() => import('./screens/Auth.tsx'), 'Auth');
const Study = lazyRouteComponent(() => import('./screens/Study.tsx'), 'Study');
const GamesLab = lazyRouteComponent(() => import('./screens/GamesLab.tsx'), 'GamesLab');
const LessonMap = lazyRouteComponent(() => import('./screens/LessonMap.tsx'), 'LessonMap');
const SetScreen = lazyRouteComponent(() => import('./screens/SetScreen.tsx'), 'SetScreen');
const WordPage = lazyRouteComponent(() => import('./screens/WordPage.tsx'), 'WordPage');
const Stickers = lazyRouteComponent(() => import('./screens/Stickers.tsx'), 'Stickers');
const Films = lazyRouteComponent(() => import('./screens/Films.tsx'), 'Films');
const FilmPlayer = lazyRouteComponent(() => import('./screens/FilmPlayer.tsx'), 'FilmPlayer');

// Called once after the app's first render (see main.tsx) to warm every lazy route chunk during
// idle time. Stops navigate with `nav()` (lib/pathNav.ts), not `<Link>`, so router-level
// `intent` preloading never fires for them — this is what keeps a tap on a stop from being the
// first request for its chunk.
const LAZY_ROUTE_COMPONENTS = [
  Play,
  PracticeRunner,
  StoryPlayer,
  Result,
  Parent,
  Auth,
  Study,
  GamesLab,
  LessonMap,
  SetScreen,
  WordPage,
  Stickers,
  Films,
  FilmPlayer,
];
export function preloadLazyRoutes(): void {
  for (const comp of LAZY_ROUTE_COMPONENTS) {
    (comp as unknown as { preload?: () => void }).preload?.();
  }
}

const rootRoute = createRootRoute({
  component: () => (
    <>
      <Outlet />
      <DiagPanel />
    </>
  ),
});

const ORIGINS: Origin[] = ['path', 'set', 'lesson', 'home', 'word', 'games'];
const parseOrigin = (v: unknown): Origin | undefined =>
  typeof v === 'string' && (ORIGINS as string[]).includes(v) ? (v as Origin) : undefined;

const routes = [
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/',
    validateSearch: (s: Record<string, unknown>): { add?: '1' } =>
      s.add === '1' ? { add: '1' } : {},
    component: Entry,
  }),
  createRoute({ getParentRoute: () => rootRoute, path: '/welcome', component: Landing }),
  createRoute({ getParentRoute: () => rootRoute, path: '/auth', component: Auth }),
  createRoute({ getParentRoute: () => rootRoute, path: '/home', component: Home }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/path',
    // `after`: the progress key of the stop just played, so the path resumes from there.
    validateSearch: (s: Record<string, unknown>): { mode?: PathMode; after?: string } => ({
      ...(isPathMode(s.mode) ? { mode: s.mode } : {}),
      ...(typeof s.after === 'string' ? { after: s.after } : {}),
    }),
    component: Path,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/word/$lessonId/$setId/$char',
    component: WordPage,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/games',
    validateSearch: (s: Record<string, unknown>): { set?: string } =>
      typeof s.set === 'string' ? { set: s.set } : {},
    component: GamesLab,
  }),
  createRoute({ getParentRoute: () => rootRoute, path: '/stickers', component: Stickers }),
  createRoute({ getParentRoute: () => rootRoute, path: '/films', component: Films }),
  createRoute({ getParentRoute: () => rootRoute, path: '/film/$n', component: FilmPlayer }),
  createRoute({ getParentRoute: () => rootRoute, path: '/study', component: Study }),
  createRoute({ getParentRoute: () => rootRoute, path: '/lesson/$lessonId', component: LessonMap }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/lesson/$lessonId/set/$setId',
    component: SetScreen,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/play/$lessonId/$module',
    validateSearch: (
      s: Record<string, unknown>,
    ): { set?: string; char?: string; game?: GameId; from?: Origin } => {
      const out: { set?: string; char?: string; game?: GameId; from?: Origin } = {};
      if (typeof s.set === 'string') out.set = s.set;
      if (typeof s.char === 'string') out.char = s.char;
      if (isGameId(s.game)) out.game = s.game;
      const from = parseOrigin(s.from);
      if (from) out.from = from;
      return out;
    },
    component: Play,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/story/$lessonId/$storyId',
    validateSearch: (s: Record<string, unknown>): { mode?: 'watch'; from?: Origin } => {
      const out: { mode?: 'watch'; from?: Origin } = {};
      if (s.mode === 'watch') out.mode = 'watch';
      const from = parseOrigin(s.from);
      if (from) out.from = from;
      return out;
    },
    component: StoryPlayer,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/practice/$lessonId/$practiceId',
    validateSearch: (s: Record<string, unknown>): { from?: Origin } => {
      const from = parseOrigin(s.from);
      return from ? { from } : {};
    },
    component: PracticeRunner,
  }),
  createRoute({ getParentRoute: () => rootRoute, path: '/result', component: Result }),
  createRoute({ getParentRoute: () => rootRoute, path: '/parent', component: Parent }),
];

export const router = createRouter({
  routeTree: rootRoute.addChildren(routes),
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
